import axios from 'axios'
import { ChainInfo } from '@safe-global/safe-apps-sdk'
import { CHAINS } from './chains'

enum PROVIDER {
  SOURCIFY = 1,
  GATEWAY = 2,
  BLOCKSCOUT = 3,
  SCANAPI = 4,
  BLOCKSCOUT_V2 = 5,
}

type SourcifyResponse = {
  name: string
  path: string
  content: string
}

const METADATA_FILE = 'metadata.json'
const DEFAULT_TIMEOUT = 10000

const getProviderURL = (
  chain: string,
  address: string,
  urlProvider: PROVIDER,
): string | undefined => {
  switch (urlProvider) {
    case PROVIDER.SOURCIFY:
      return `https://sourcify.dev/server/files/${chain}/${address}`
    case PROVIDER.GATEWAY:
      const baseUrl = CHAINS[chain]?.cgwUrl
        ? CHAINS[chain]?.cgwUrl
        : 'https://prod.safe-client-gateway.keypersafe.xyz'
      const url = `${baseUrl}/v1/chains/${chain}/contracts/${address}`
      return url
    case PROVIDER.BLOCKSCOUT:
      const blockscoutUrl = CHAINS[chain]?.blockscoutUrl
      if (!blockscoutUrl) return undefined

      return `${blockscoutUrl}/api?module=contract&action=getabi&address=${address}`
    case PROVIDER.BLOCKSCOUT_V2:
      const blockscoutV2Url = CHAINS[chain]?.blockscoutV2Url
      if (!blockscoutV2Url) return undefined

      return `${blockscoutV2Url}/api/v2/smart-contracts/${address}`
    case PROVIDER.SCANAPI:
      const scanAPI = CHAINS[chain]?.scanApi
      if (!scanAPI) return undefined

      /** @notice adding chainid for compatibility with Etherscan V2 API */
      return `${scanAPI?.url}/api?chainid=${chain}&module=contract&action=getabi&address=${address}&apikey=${scanAPI?.apiKey}`

    default:
      throw new Error('The Provider is not supported')
  }
}

const getAbiFromSourcify = async (address: string, chainId: string): Promise<any> => {
  const url = getProviderURL(chainId, address, PROVIDER.SOURCIFY)
  if (!url) throw new Error(`Sourcify config not set for ${chainId}`)

  const { data } = await axios.get<SourcifyResponse[]>(url, { timeout: DEFAULT_TIMEOUT })

  if (data.length) {
    const metadata = data.find((item: SourcifyResponse) => item.name === METADATA_FILE)
    return metadata && JSON.parse(metadata.content)?.output?.abi
  }

  throw new Error('Contract found but could not found abi using Sourcify')
}

const getAbiFromGateway = async (address: string, chainName: string): Promise<any> => {
  const url = getProviderURL(chainName, address, PROVIDER.GATEWAY)
  if (!url) throw new Error(`Gateway config not set for ${chainName}`)

  const { data } = await axios.get(url, { timeout: DEFAULT_TIMEOUT })

  // We need to check if the abi is present in the response because it's possible
  // That the transaction service just stores the contract and returns 200 without querying for the abi
  // (or querying for the abi failed)
  if (data && data.contractAbi?.abi) {
    return data?.contractAbi?.abi
  }

  throw new Error('Contract found but could not found ABI using the Gateway')
}

const getAbiFromBlockscout = async (address: string, chainId: string): Promise<any> => {
  const url = getProviderURL(chainId, address, PROVIDER.BLOCKSCOUT)
  if (!url) throw new Error(`Blockscout config not set for ${chainId}`)

  const { data } = await axios.get(url, { timeout: DEFAULT_TIMEOUT })

  if (data && data.message === 'OK' && data.result) {
    return JSON.parse(data.result)
  }

  throw new Error('Contract found but could not found ABI using Blockscout')
}

const getAbiFromBlockscoutV2 = async (address: string, chainId: string): Promise<any> => {
  const url = getProviderURL(chainId, address, PROVIDER.BLOCKSCOUT_V2)
  if (!url) throw new Error(`Blockscout V2 config not set for ${chainId}`)

  const { data } = await axios.get(url, { timeout: DEFAULT_TIMEOUT })

  if (data && data.abi) {
    return data.abi
  }

  throw new Error('Contract found but could not found ABI using Blockscout v2')
}

const getABIFromScanAPI = async (address: string, chainId: string): Promise<any> => {
  const url = getProviderURL(chainId, address, PROVIDER.SCANAPI)
  if (!url) throw new Error(`Scan API config not set for ${chainId}`)

  const { data } = await axios.get(url, { timeout: DEFAULT_TIMEOUT })

  if (data && data.message === 'OK' && data.result) {
    return JSON.parse(data.result)
  }

  throw new Error('Contract found but ABI is missing when using API service')
}

const getAbi = async (address: string, chainInfo: ChainInfo): Promise<any> => {
  let abi
  try {
    abi = await Promise.any([
      getAbiFromSourcify(address, chainInfo.chainId),
      getAbiFromGateway(address, chainInfo.chainId),
      getAbiFromBlockscout(address, chainInfo.chainId),
      getAbiFromBlockscoutV2(address, chainInfo.chainId),
      getABIFromScanAPI(address, chainInfo.chainId),
    ])
  } catch {
    abi = null
  }
  return abi
}

export default getAbi
